<script setup lang="ts">
import WaveSurfer from 'wavesurfer.js';

const props = defineProps<{ url: string, isDownloadable?: boolean }>();
const waveformRef = useTemplateRef('waveformRef');
const isPlaying = ref(false);
const isDownloading = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const speedSteps = [1, 1.5, 2] as const;
const speedState = ref<typeof speedSteps[number]>(1);
let wavesurfer: WaveSurfer | undefined;


function handleOnPlay() {
  if (!wavesurfer) return;
  isPlaying.value = true;
  wavesurfer.play();
};

function handleOnPause() {
  if (!wavesurfer) return;
  isPlaying.value = false;
  wavesurfer.pause();
};

function handleOnDownload() {
    isDownloading.value = true;
};

function handleSetSpeed() {
  if (!wavesurfer) return;
  const nextIndex = (speedSteps.indexOf(speedState.value) + 1) % speedSteps.length;
  speedState.value = speedSteps[nextIndex] || 1;
  wavesurfer.setPlaybackRate(speedState.value);
};

function formatTime(time: number) {
  const minutes = Math.floor(time / 60).toString();
  const seconds = Math.floor(time % 60).toString();
  return `${ minutes }:${ seconds.padStart(2, '0') }`;
};


onMounted(() => {
  if (!waveformRef.value) return;
  
  wavesurfer = WaveSurfer.create({
    container: waveformRef.value,
    height: 25,
    barWidth: 5,
    barHeight: 25,
    barRadius: 50,
    waveColor: '#D0BFE8',
    progressColor: '#8C62C6',
  });

  wavesurfer.load(props.url);
  wavesurfer.on('finish', () => { isPlaying.value = false });
  wavesurfer.on('ready', () => { duration.value = wavesurfer!.getDuration() });
  wavesurfer.on('timeupdate', time => { currentTime.value = time });
});

onUnmounted(() => {
  wavesurfer?.destroy();
});
</script>

<template>
  <div 
    class="bg-purple-50 flex items-center justify-between rounded-lg h-10 px-2 transition-all duration-300 
        text-purple-500 select-none"
  >
    <div class="flex items-center gap-2">
      <Icon
        v-if="isDownloading"
        name="svg-spinners:90-ring"
        size="24"
        class="cursor-pointer"
      />
      <Icon
        v-else-if="props.isDownloadable"
        name="solar:download-square-linear"
        size="24"
        class="cursor-pointer"
        @click="handleOnDownload"
      />
      <div
        class="h-5 aspect-square flex-center items-end rounded-[5px] cursor-pointer border-2! border-purple-500! pt-px lg:pt-0.5"
        :class="speedState !== 1 ? 'px-1 bg-purple-500 text-white' : 'w-5'"
        @click="handleSetSpeed"
      >
        <span class="text-caption">x{{ speedState }}</span>
      </div>

      <span class="px-3 text-caption text-gray-400">{{ formatTime(duration) }}</span>
    </div>

    <div  class="w-full px-2 cursor-pointer"><div ref="waveformRef" /></div>

    <div class="flex items-center">
      <span class="px-2 text-caption text-gray-600">{{ formatTime(currentTime) }}</span>
      <Icon
        v-if="isPlaying"
        name="solar:pause-circle-linear"
        size="24"
        class="cursor-pointer"
        @click="handleOnPause"
      />
      <Icon
        v-else
        name="solar:play-circle-linear"
        size="24"
        class="cursor-pointer"
        @click="handleOnPlay"
      />
    </div>
  </div>
</template>